import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"
import {
  normalizeApiFlag,
  normalizeSuggestionsByTone,
} from "../../components/ai-community-inbox/utils/messageSuggestions"

// GET /api/v1/messages/suggestion/{messageId}
export const fetchMessageSuggestions = createAsyncThunk(
  "messageSuggestions/fetch",
  async ({ interactionId }, { rejectWithValue, getState }) => {
    try {
      const token = getState().auth?.token
      if (!token) throw new Error("Authentication required")

      if (!interactionId) {
        throw new Error("interactionId is required")
      }

      const data = await get(`messages/${interactionId}/suggestions`)

      return {
        interactionId,
        data,
      }
    } catch (err) {
      return rejectWithValue(
        err?.message || "Failed to fetch message suggestions"
      )
    }
  },
  {
    condition: ({ interactionId, force } = {}, { getState }) => {
      if (force) return true
      const intId = interactionId != null ? String(interactionId).trim() : ""
      if (!intId) return false
      const s = getState().messageSuggestions
      if (s.status === "loading" && String(s.pendingInteractionId ?? "") === intId) {
        return false
      }
      if (s.status === "succeeded" && String(s.interactionId ?? "") === intId) {
        return false
      }
      if (s.status === "failed" && String(s.interactionId ?? "") === intId) {
        return false
      }
      return true
    },
  },
)

const initialState = {
  interactionId: null,
  /** Set while `fetchMessageSuggestions` is in flight so UI can show title loading for that interaction only. */
  pendingInteractionId: null,
  suggestions: {
    formal: [],
    neutral: [],
    casual: [],
  },
  canRespond: false,
  needsPropertyHelp: false,
  triageDecision: null,
  contextTitle: null,
  sourceLabel: null,
  reason: null,
  source: null,
  internalAlert: null,
  status: "idle",
  error: null,
}

const messageSuggestionsSlice = createSlice({
  name: "messageSuggestions",
  initialState,
  reducers: {
    clearMessageSuggestions: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMessageSuggestions.pending, (state, action) => {
        state.status = "loading"
        state.error = null
        const nextId =
          action.meta?.arg?.interactionId != null
            ? String(action.meta.arg.interactionId)
            : null
        state.pendingInteractionId = nextId
        const prevId = state.interactionId != null ? String(state.interactionId) : null
        // Drop previous thread's suggestion/reason so the reply bar stays empty until this interaction returns.
        if (nextId && prevId !== nextId) {
          state.interactionId = null
          state.suggestions = { formal: [], neutral: [], casual: [] }
          state.canRespond = false
          state.needsPropertyHelp = false
          state.triageDecision = null
          state.contextTitle = null
          state.sourceLabel = null
          state.reason = null
          state.source = null
          state.internalAlert = null
        }
      })
      .addCase(fetchMessageSuggestions.fulfilled, (state, action) => {
        state.status = "succeeded"
        state.pendingInteractionId = null
        state.interactionId = action.payload.interactionId
        const d = action.payload.data || {}
        state.suggestions = normalizeSuggestionsByTone(d.suggestions_by_tone)
        state.canRespond = normalizeApiFlag(d.can_respond)
        state.needsPropertyHelp = normalizeApiFlag(d.needs_property_help)
        state.triageDecision = d.triage_decision ?? null
        state.contextTitle = d.context_title ?? null
        state.sourceLabel = d.source_label ?? null
        state.reason = d.reason ?? null
        state.source = d.source ?? null
        state.internalAlert = d.internal_alert ?? null
      })
      .addCase(fetchMessageSuggestions.rejected, (state, action) => {
        state.status = "failed"
        state.pendingInteractionId = null
        state.error = action.payload
      })
  },
})

export const { clearMessageSuggestions } = messageSuggestionsSlice.actions
export default messageSuggestionsSlice.reducer
